function initializeAiSupportChat(chat) {
  if (!chat || chat.dataset.initialized === "true") return;

  chat.dataset.initialized = "true";

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
  };

  const baseUrl = document.querySelector("input[data-base-url]")?.value || "";
  const chatEndpoint = chat.dataset.chatEndpoint || "/api/fbapp/api/smart_reply/chat";
  const toggleButton = chat.querySelector("[data-chat-toggle]");
  const closeButton = chat.querySelector("[data-chat-close]");
  const panel = chat.querySelector("[data-chat-panel]");
  const form = chat.querySelector("[data-chat-form]");
  const input = chat.querySelector("[data-chat-input]");
  const sendButton = chat.querySelector("[data-chat-send]");
  const emptyState = chat.querySelector("[data-chat-empty]");
  const messages = chat.querySelector("[data-chat-messages]");
  const quickActions = chat.querySelector("[data-chat-quick-actions]");
  const scrollArea = chat.querySelector("[data-chat-scroll-area]");
  const bodyScrollLockApi = window.bodyScrollLock;
  const logisticsEndpoint = chat.dataset.logisticsEndpoint || "/api/fbapp/api/smart_reply/search_logistics";
  const shipTimeEndpoint = chat.dataset.shipTimeEndpoint || "/api/fbapp/api/smart_reply/search_ship_time";
  let hasConversation = chat.dataset.hasConversation === "true";
  let chatRequestNum = 1;
  let conversationId = String(chat.dataset.conversationId || "").trim();
  let usesFallbackBodyLock = false;

  const buildRequestUrl = (path) => {
    if (!baseUrl) return path;

    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return `${normalizedBaseUrl}${normalizedPath}`;
  };

  const updateComposerState = () => {
    if (!sendButton) return;
    sendButton.disabled = !input?.value?.trim();
  };

  const isMobileViewport = () => window.matchMedia("(max-width: 749px)").matches;

  const lockPageScroll = () => {
    if (!panel || !isMobileViewport()) return;

    if (bodyScrollLockApi?.disableBodyScroll) {
      bodyScrollLockApi.disableBodyScroll(panel, {
        reserveScrollBarGap: true,
        allowTouchMove: (element) =>
          Boolean(element?.closest("[data-chat-scroll-area]"))
      });
      return;
    }

    if (!document.body.classList.contains("overflow-hidden")) {
      document.body.classList.add("overflow-hidden");
      usesFallbackBodyLock = true;
    }
  };

  const unlockPageScroll = () => {
    if (panel && bodyScrollLockApi?.enableBodyScroll) {
      bodyScrollLockApi.enableBodyScroll(panel);
    }

    if (usesFallbackBodyLock) {
      document.body.classList.remove("overflow-hidden");
      usesFallbackBodyLock = false;
    }
  };

  const updateEmptyState = () => {
    const hasMessages = Boolean(messages?.children.length);
    hasConversation = hasConversation || hasMessages;
    chat.dataset.hasConversation = hasConversation ? "true" : "false";

    if (emptyState) {
      emptyState.hidden = hasConversation;
    }

    if (messages) {
      messages.hidden = !hasMessages;
    }
  };

  const scrollMessagesToBottom = () => {
    if (!scrollArea) return;

    requestAnimationFrame(() => {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    });
  };

  const SUPPORT_EMAIL = "support@shop.fbrq.com";
  const LOYALTY_PROGRAM_URL = "https://www.fbrq.com/pages/loyalty-program";
  const GIFT_CARD_URL = "https://www.fbrq.com/products/fabrique-digital-gift-card";
  const RETURNS_EXCHANGES_URL = "returns.fbrq.com";
  const MESSAGE_LINKS = [
    {
      value: SUPPORT_EMAIL,
      href: `mailto:${SUPPORT_EMAIL}`,
      text: SUPPORT_EMAIL,
      ariaLabel: `Email ${SUPPORT_EMAIL}`,
      className: "ai-support-chat__email-link",
    },
    {
      value: LOYALTY_PROGRAM_URL,
      href: LOYALTY_PROGRAM_URL,
      text: LOYALTY_PROGRAM_URL,
      ariaLabel: "Open Fabrique loyalty program",
    },
    {
      value: GIFT_CARD_URL,
      href: GIFT_CARD_URL,
      text: GIFT_CARD_URL,
      ariaLabel: "Open Fabrique digital gift card",
    },
     {
      value: RETURNS_EXCHANGES_URL,
      href: `https://${RETURNS_EXCHANGES_URL}`,
      text: RETURNS_EXCHANGES_URL,
      ariaLabel: "Returns & Exchanges",
    },
  ];

  const QUICK_ACTIONS = {
    when_ship: {
      label: "When will my order ship?",
      intro: "I’d be happy to check your order’s shipping status. Enter your order email and order number to continue.",
      endpoint: shipTimeEndpoint,
    },
    track_order: {
      label: "Track my order.",
      intro: "I can help you track your order. Enter your order email and order number to continue.",
      endpoint: logisticsEndpoint,
    },
  };

  const escapeForRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const createMessageLink = (value) => {
    const linkConfig = MESSAGE_LINKS.find((item) => item.value === value);
    if (!linkConfig) return document.createTextNode(value);

    const link = document.createElement("a");
    link.className = "ai-support-chat__message-link";
    link.href = linkConfig.href;
    link.textContent = linkConfig.text;
    link.setAttribute("aria-label", linkConfig.ariaLabel);

    if (linkConfig.className) {
      link.classList.add(linkConfig.className);
    }

    return link;
  };

  const normalizeAssistantMessage = (message) => (
    String(message || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/\\n/g, "\n")
  );

  const appendFormattedAssistantMessage = (parent, text) => {
    const normalizedText = normalizeAssistantMessage(text);
    const matchableLinks = MESSAGE_LINKS
      .map((item) => item.value)
      .sort((firstValue, secondValue) => secondValue.length - firstValue.length);
    const linkPattern = new RegExp(
      matchableLinks.map((value) => escapeForRegex(value)).join("|"),
      "g"
    );
    let cursor = 0;
    let match = linkPattern.exec(normalizedText);

    while (match) {
      const matchedValue = match[0];
      const startIndex = match.index;

      if (startIndex > cursor) {
        parent.appendChild(document.createTextNode(normalizedText.slice(cursor, startIndex)));
      }

      parent.appendChild(createMessageLink(matchedValue));
      cursor = startIndex + matchedValue.length;
      match = linkPattern.exec(normalizedText);
    }

    if (cursor < normalizedText.length) {
      parent.appendChild(document.createTextNode(normalizedText.slice(cursor)));
    }
  };

  const addMessage = (message, role = "assistant", isError = false) => {
    if (!message || !messages) return;

    const item = document.createElement("div");
    item.className = `ai-support-chat__message ai-support-chat__message--${role}`;

    if (isError) {
      item.classList.add("ai-support-chat__message--error");
    }

    const paragraph = document.createElement("p");
    if (role === "assistant") {
      appendFormattedAssistantMessage(paragraph, message);
    } else {
      paragraph.textContent = message;
    }
    item.appendChild(paragraph);

    messages.appendChild(item);
    updateEmptyState();
    scrollMessagesToBottom();
  };

  const addAssistantNodeMessage = (contentNode, options = {}) => {
    const target = options.container || messages;
    if (!contentNode || !target) return null;

    const item = document.createElement("div");
    item.className = "ai-support-chat__message ai-support-chat__message--assistant ai-support-chat__message--interactive";
    item.appendChild(contentNode);

    target.appendChild(item);
    updateEmptyState();

    if (target === messages && options.scroll !== false) {
      scrollMessagesToBottom();
    }

    return item;
  };

  const createHtmlFragment = (html) => {
    const template = document.createElement("template");
    template.innerHTML = String(html || "").trim();
    template.content.querySelectorAll("script").forEach((script) => script.remove());
    return template.content;
  };

  const addHtmlResponseMessage = (html) => {
    if (!messages || !html) return null;

    const wrapper = document.createElement("div");
    wrapper.className = "ai-support-chat__html-response";
    wrapper.appendChild(createHtmlFragment(html));

    return addAssistantNodeMessage(wrapper);
  };

  const getQuickActionConfig = (actionKey) => QUICK_ACTIONS[actionKey] || null;

  const renderLogisticsForm = (actionKey) => {
    const actionConfig = getQuickActionConfig(actionKey);
    if (!actionConfig) return null;

    const card = document.createElement("div");
    card.className = "ai-support-chat__logistics-card";

    const intro = document.createElement("p");
    intro.className = "ai-support-chat__logistics-intro";
    intro.textContent = actionConfig.intro;
    card.appendChild(intro);

    const form = document.createElement("form");
    form.className = "ai-support-chat__logistics-form";
    form.setAttribute("data-chat-logistics-form", "");
    form.dataset.chatLogisticsAction = actionKey;

    const emailField = document.createElement("label");
    emailField.className = "ai-support-chat__logistics-field";

    const emailLabel = document.createElement("span");
    emailLabel.className = "ai-support-chat__logistics-label";
    emailLabel.textContent = "* Email Address :";

    const emailInput = document.createElement("input");
    emailInput.className = "ai-support-chat__logistics-input";
    emailInput.type = "email";
    emailInput.name = "receiverEmail";
    emailInput.autocomplete = "email";
    emailInput.required = true;

    emailField.appendChild(emailLabel);
    emailField.appendChild(emailInput);

    const orderField = document.createElement("label");
    orderField.className = "ai-support-chat__logistics-field";

    const orderLabel = document.createElement("span");
    orderLabel.className = "ai-support-chat__logistics-label";
    orderLabel.textContent = "* Order Number (Begin with “4R7”) :";

    const orderInput = document.createElement("input");
    orderInput.className = "ai-support-chat__logistics-input";
    orderInput.type = "text";
    orderInput.name = "shopifyOrderNum";
    orderInput.autocomplete = "off";
    orderInput.required = true;

    orderField.appendChild(orderLabel);
    orderField.appendChild(orderInput);

    const submitButton = document.createElement("button");
    submitButton.type = "submit";
    submitButton.className = "ai-support-chat__logistics-submit";
    submitButton.setAttribute("data-chat-logistics-submit", "");
    submitButton.disabled = true;
    submitButton.textContent = "FIND MY ORDER";

    form.appendChild(emailField);
    form.appendChild(orderField);
    form.appendChild(submitButton);
    card.appendChild(form);

    const updateSubmitState = () => updateLogisticsSubmitState(form);
    form.addEventListener("input", updateSubmitState);
    form.addEventListener("change", updateSubmitState);
    updateLogisticsSubmitState(form);

    return addAssistantNodeMessage(card, {
      container: messages,
      scroll: false,
    });
  };

  const updateLogisticsSubmitState = (form) => {
    if (!form) return;

    const submitButton = form.querySelector("[data-chat-logistics-submit]");
    const emailInput = form.querySelector('input[name="receiverEmail"]');
    const orderInput = form.querySelector('input[name="shopifyOrderNum"]');
    const isLoading = form.dataset.isLoading === "true";
    const canSubmit = Boolean(String(emailInput?.value || "").trim() && String(orderInput?.value || "").trim());

    if (submitButton) {
      submitButton.disabled = isLoading || !canSubmit;
    }
  };

  const setLogisticsSubmitState = (form, isLoading) => {
    if (!form) return;

    form.dataset.isLoading = isLoading ? "true" : "false";

    form.querySelectorAll("input").forEach((field) => {
      field.disabled = isLoading;
    });

    updateLogisticsSubmitState(form);
  };

  const submitLogisticsSearch = async (formElement, actionKey) => {
    if (!formElement || panel?.classList.contains("is-loading")) return;

    const actionConfig = getQuickActionConfig(actionKey);
    if (!actionConfig) return;
    const actionEndpoint = actionConfig.endpoint || logisticsEndpoint;

    const formData = new FormData(formElement);
    const receiverEmail = String(formData.get("receiverEmail") || "").trim();
    const shopifyOrderNum = String(formData.get("shopifyOrderNum") || "").trim();

    if (!receiverEmail || !shopifyOrderNum) {
      formElement.reportValidity?.();
      return;
    }

    const payload = {
      receiverEmail,
      shopifyOrderNum,
    };

    setLogisticsSubmitState(formElement, true);
    setLoading(true);
    const loadingMessage = addLoadingMessage();

    try {
      const response = await fetch(buildRequestUrl(actionEndpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      let html = "";

      if (contentType.includes("application/json")) {
        const data = await response.json();
        html = String(data?.answer || data?.data?.html).trim();
      } else {
        html = (await response.text()).trim();
      }

      removeLoadingMessage(loadingMessage);
      addHtmlResponseMessage(html || "<p>Unable to load order details.</p>");
    } catch (error) {
      removeLoadingMessage(loadingMessage);
      addMessage("Sorry, something went wrong. Please try again.", "assistant", true);
      console.error("[AI Support Chat]", error);
    } finally {
      setLogisticsSubmitState(formElement, false);
      setLoading(false);
    }
  };

  const updateConversationId = (nextConversationId) => {
    const normalizedConversationId = String(nextConversationId || "").trim();
    if (!normalizedConversationId) return;

    conversationId = normalizedConversationId;
    chat.dataset.conversationId = normalizedConversationId;
  };

  const addLoadingMessage = () => {
    if (!messages) return null;

    const item = document.createElement("div");
    item.className = "ai-support-chat__message ai-support-chat__message--assistant ai-support-chat__message--loading";
    item.setAttribute("data-chat-loading-message", "true");
    item.setAttribute("aria-hidden", "true");

    const paragraph = document.createElement("p");
    paragraph.textContent = "Thinking...";
    item.appendChild(paragraph);

    messages.appendChild(item);
    updateEmptyState();
    scrollMessagesToBottom();

    return item;
  };

  const removeLoadingMessage = (loadingMessage) => {
    if (!loadingMessage?.parentNode) return;

    loadingMessage.parentNode.removeChild(loadingMessage);
    updateEmptyState();
    scrollMessagesToBottom();
  };

  const setLoading = (isLoading) => {
    if (!panel || !sendButton) return;

    panel.classList.toggle("is-loading", isLoading);
    sendButton.disabled = isLoading || !input?.value?.trim();
  };

  const openChat = () => {
    chat.setAttribute("open", "");
    if (panel) panel.hidden = false;
    if (toggleButton) toggleButton.setAttribute("aria-expanded", "true");
    lockPageScroll();
    requestAnimationFrame(() => input?.focus());
  };

  const closeChat = () => {
    chat.removeAttribute("open");
    if (panel) panel.hidden = true;
    if (toggleButton) toggleButton.setAttribute("aria-expanded", "false");
    unlockPageScroll();
  };

  quickActions?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-chat-quick-question]");
    if (!button) return;

    const actionKey = button.getAttribute("data-chat-quick-question");
    const actionConfig = getQuickActionConfig(actionKey);
    if (!actionConfig) return;

    hasConversation = true;
    addMessage(actionConfig.label, "user");
    renderLogisticsForm(actionKey);
    scrollMessagesToBottom();
  });

  toggleButton?.addEventListener("click", openChat);
  closeButton?.addEventListener("click", closeChat);
  input?.addEventListener("input", updateComposerState);

  messages?.addEventListener("submit", (event) => {
    const logisticsForm = event.target.closest("[data-chat-logistics-form]");
    if (!logisticsForm) return;

    event.preventDefault();
    const actionKey = logisticsForm.dataset.chatLogisticsAction || "";
    submitLogisticsSearch(logisticsForm, actionKey);
  });

  form?.addEventListener("submit", async (event) => {
    event.preventDefault();

    const message = input?.value?.trim();
    if (!message || panel?.classList.contains("is-loading")) return;

    const countryCode = "US";
    const languageCode = "en";
    const requestNum = chatRequestNum++;
    const uid = getCookie('my_client_id') || getCookie('shopify_client_id');
    const payload = {
      message,
      country: countryCode,
      language: languageCode,
      num: requestNum,
      uid
    };

    if (conversationId) {
      payload.conversationId = conversationId;
    }

    hasConversation = true;
    addMessage(message, "user");
    form.reset();
    updateComposerState();
    updateEmptyState();
    setLoading(true);
    const loadingMessage = addLoadingMessage();

    try {
      const response = await fetch(buildRequestUrl(chatEndpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();
      updateConversationId(
        data?.conversationId
      );
      const answer = data?.answer?.trim();

      removeLoadingMessage(loadingMessage);
      if (answer) {
        addMessage(answer, "assistant");
      }
    } catch (error) {
      removeLoadingMessage(loadingMessage);
      addMessage("Sorry, something went wrong. Please try again.", "assistant", true);
      console.error("[AI Support Chat]", error);
    } finally {
      setLoading(false);
      input?.focus();
    }
  });

  document.addEventListener("click", (event) => {
    if (chat.hasAttribute("open") && !chat.contains(event.target)) {
      closeChat();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.code?.toUpperCase() === "ESCAPE" && chat.hasAttribute("open")) {
      closeChat();
    }
  });

  window.addEventListener("resize", () => {
    if (!chat.hasAttribute("open")) return;

    if (isMobileViewport()) {
      lockPageScroll();
    } else {
      unlockPageScroll();
    }
  });

  updateComposerState();
  updateEmptyState();
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-ai-support-chat]").forEach(initializeAiSupportChat);
});
